/**
 * Google Analytics 4 with Consent Mode v2, off until configured and consented.
 *
 * Nothing here runs unless a measurement ID is set on the admin Settings page (read
 * from the public settings endpoint; see `resolveMeasurementId`) and the visitor
 * pressed Accept. Events never carry personal data: callers pass only
 * a fixed form name.
 */

/** localStorage key for the visitor's choice. First-party only; never sent anywhere. */
export const CONSENT_KEY = "wch_analytics_consent";
/** Window event that reopens the consent banner ("Cookie settings"). */
export const OPEN_SETTINGS_EVENT = "wch:cookie-settings";

/** What a visitor can press in the cookie box. */
export type ConsentChoice = "granted" | "denied";

/**
 * What is stored. `granted-v2` is an acceptance of the text that names the map;
 * `granted` is an older acceptance that covered Google Analytics only.
 */
export type StoredConsent = "granted-v2" | "granted" | "denied";

/** The stored choice, or "none" when the visitor has not chosen. */
export type ConsentState = StoredConsent | "none";

/** True when Google Analytics may run (any acceptance, including the older one). */
export function analyticsAllowed(state: ConsentState | "pending"): boolean {
    return state === "granted-v2" || state === "granted";
}

/** True when the Google map may load (only an acceptance that named the map). */
export function mapsAllowed(state: ConsentState | "pending"): boolean {
    return state === "granted-v2";
}

type GtagWindow = Window & {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: string]: unknown;
};

const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;

/**
 * The GA4 measurement ID to use, or "" when analytics is off. The only source is the
 * admin value (from the public settings endpoint); an empty or malformed value, or an
 * API that is down, turns analytics off.
 */
export function resolveMeasurementId(adminValue: string | undefined | null): string {
    const id = (adminValue ?? "").trim();
    return MEASUREMENT_ID_PATTERN.test(id) ? id : "";
}

const MAPS_KEY_PATTERN = /^AIza[0-9A-Za-z_-]{35}$/;

/** The Google Maps embed key to use, or "" when the map is off (empty or malformed value, or API down). */
export function resolveMapsKey(adminValue: string | undefined | null): string {
    const key = (adminValue ?? "").trim();
    return MAPS_KEY_PATTERN.test(key) ? key : "";
}

/** The stored choice, or null when the visitor has not chosen (or storage is blocked). */
export function readConsent(): StoredConsent | null {
    try {
        const value = window.localStorage.getItem(CONSENT_KEY);
        return value === "granted-v2" || value === "granted" || value === "denied" ? value : null;
    } catch {
        return null;
    }
}

/** Stores the choice; returns false when storage is blocked (the choice then lasts for the page only). */
export function writeConsent(choice: StoredConsent): boolean {
    try {
        window.localStorage.setItem(CONSENT_KEY, choice);
        return true;
    } catch {
        return false;
    }
}

function gtagWindow(): GtagWindow {
    return window as unknown as GtagWindow;
}

let loaded = false;
let allowed = false;

/**
 * Loads gtag.js with consent denied by default, then grants it. Call only after Accept.
 * Safe to call more than once.
 */
export function enableAnalytics(id: string): void {
    const w = gtagWindow();
    allowed = true;
    w[`ga-disable-${id}`] = false;
    if (loaded) {
        w.gtag?.("consent", "update", { analytics_storage: "granted" });
        return;
    }
    loaded = true;
    w.dataLayer = w.dataLayer || [];
    w.gtag = function gtag() {
        // gtag.js requires the real `arguments` object, not a rest array.
        // eslint-disable-next-line prefer-rest-params
        w.dataLayer!.push(arguments);
    };
    w.gtag("consent", "default", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
    });
    w.gtag("js", new Date());
    // Page views are sent by trackPageView so client-side navigations count too.
    w.gtag("config", id, {
        send_page_view: false,
        allow_google_signals: false,
        // Host-only cookies, so they can always be removed again.
        cookie_domain: "none",
    });
    w.gtag("consent", "update", { analytics_storage: "granted" });
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
}

/** Removes every `_ga` / `_ga_<id>` cookie for this host and each parent domain. */
export function clearAnalyticsCookies(): void {
    const names = document.cookie
        .split(";")
        .map((part) => part.split("=")[0].trim())
        .filter((name) => name === "_ga" || name.startsWith("_ga_"));
    if (names.length === 0) return;
    const parts = window.location.hostname.split(".");
    const domains: (string | null)[] = [null]; // null = host-only
    for (let i = 0; i < parts.length - 1; i++) {
        domains.push(parts.slice(i).join("."), "." + parts.slice(i).join("."));
    }
    for (const name of names) {
        for (const domain of domains) {
            document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
        }
    }
}

/** Stops sending events and removes the GA cookies (also ones left by an earlier visit). */
export function disableAnalytics(id: string): void {
    const w = gtagWindow();
    allowed = false;
    clearAnalyticsCookies();
    if (!loaded) return;
    w[`ga-disable-${id}`] = true;
    w.gtag?.("consent", "update", { analytics_storage: "denied" });
}

/** What a `page_view` event carries. */
export type PageViewParams = { page_location: string; page_path: string; page_title: string };

/**
 * The location, path and title to send for a page. Listing pages send no
 * listing content (the address and price are in the slug and the title): a
 * listing page sends `/listings/<key>` with the title "Listing", and the search
 * page sends `/listings` (no query) with the title "Listing search". Every
 * other page sends what it shows.
 */
export function pageViewParams(
    page: { origin: string; pathname: string; href: string; title: string },
): PageViewParams {
    const listing = /^\/listings\/([^/]+)(?:\/|$)/.exec(page.pathname);
    if (listing) {
        const path = `/listings/${listing[1]}`;
        return { page_location: `${page.origin}${path}`, page_path: path, page_title: "Listing" };
    }
    if (page.pathname === "/listings" || page.pathname === "/listings/") {
        return { page_location: `${page.origin}/listings`, page_path: "/listings", page_title: "Listing search" };
    }
    return { page_location: page.href, page_path: page.pathname, page_title: page.title };
}

/** Sends a page_view for the current URL. Does nothing without consent. */
export function trackPageView(): void {
    if (!allowed || !loaded) return;
    // Deferred so the new page's title is in place after a client-side navigation.
    setTimeout(() => {
        if (!allowed) return;
        gtagWindow().gtag?.("event", "page_view", pageViewParams({
            origin: window.location.origin,
            pathname: window.location.pathname,
            href: window.location.href,
            title: document.title,
        }));
    }, 50);
}

/**
 * Sends a successful form submission as `generate_lead`. Only the fixed form name is
 * sent: never a name, email, phone or message.
 */
export function trackLead(formName: "guide_request" | "newsletter_signup"): void {
    if (!allowed || !loaded) return;
    gtagWindow().gtag?.("event", "generate_lead", { form_name: formName });
}

/** Sends a click on the hero's "Contact Leanne" button as `contact_cta`. No data is attached. */
export function trackContactCta(): void {
    if (!allowed || !loaded) return;
    gtagWindow().gtag?.("event", "contact_cta");
}

// A tiny external store so components read the stored choice without touching storage
// during server render ("pending" there and during hydration).
const listeners = new Set<() => void>();
let memoryChoice: StoredConsent | null = null;

/** Subscribes to changes of the stored choice. For useSyncExternalStore. */
export function subscribeConsent(listener: () => void): () => void {
    listeners.add(listener);
    // Another tab changed the choice: follow it.
    const onStorage = (e: StorageEvent) => {
        if (e.key === CONSENT_KEY || e.key === null) listener();
    };
    window.addEventListener("storage", onStorage);
    return () => {
        listeners.delete(listener);
        window.removeEventListener("storage", onStorage);
    };
}

/** Client snapshot: the stored choice, or "none" when the visitor has not chosen. */
export function getConsentSnapshot(): ConsentState {
    return readConsent() ?? memoryChoice ?? "none";
}

/** Server snapshot: nothing is known yet. */
export function getServerConsentSnapshot(): "pending" {
    return "pending";
}

/** Stores the choice, and whether the box named the map (in memory for this page if storage is blocked) and tells subscribers. */
export function saveConsent(choice: ConsentChoice, boxNamedMaps: boolean): void {
    // Accept is stored as `granted-v2` only when the box shown named the map; an
    // analytics-only acceptance stays `granted`, so the box asks again once a map is on.
    const stored: StoredConsent = choice === "denied" ? "denied" : boxNamedMaps ? "granted-v2" : "granted";
    if (!writeConsent(stored)) memoryChoice = stored;
    listeners.forEach((l) => l());
}
